import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Checkbox,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { axiosPrivate } from "src/common/axiosProtected";
import { addOrdersToInvoice } from "src/queries/query";

const AddOrdersToInvoiceModal = ({ open, onClose, orderId }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [error, setError] = useState("");
  const queryClient = useQueryClient();

  // Fetch invoices
  const { data: invoicesData, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ["invoices-list"],
    queryFn: async () => {
      const response = await axiosPrivate.get("/api/v1/invoices");
      return response.data?.data || [];
    },
    enabled: open,
  });

  // Filter invoices based on search term
  const filteredInvoices = useMemo(() => {
    if (!invoicesData) return [];
    return invoicesData.filter(
      (invoice) =>
        invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customerName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [invoicesData, searchTerm]);

  // Mutation for adding orders to invoice
  const { mutate: addOrders, isPending: isAdding } = useMutation({
    mutationFn: () => addOrdersToInvoice(selectedInvoice.uuid, [orderId]),
    onSuccess: () => {
      toast.success("Order added to invoice successfully!");
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoices-list"] });
      handleClose();
    },
    onError: (error) => {
      const errorMessage =
        error.message || "Failed to add order to invoice. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  const handleClose = () => {
    setSearchTerm("");
    setSelectedInvoice(null);
    setError("");
    onClose();
  };

  const handleSelectInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setError("");
  };

  const handleAddOrder = () => {
    if (!selectedInvoice) {
      setError("Please select an invoice");
      return;
    }
    addOrders();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Order to Invoice</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by invoice number, order number"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ my: 2 }}
          disabled={isLoadingInvoices || isAdding}
        />

        {isLoadingInvoices ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : filteredInvoices.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            {searchTerm ? "No invoices found matching your search." : "No invoices available."}
          </Typography>
        ) : (
          <List
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              maxHeight: 300,
              overflow: "auto",
            }}
          >
            {filteredInvoices.map((invoice) => (
              <ListItem
                key={invoice.uuid}
                disablePadding
                sx={{
                  backgroundColor:
                    selectedInvoice?.uuid === invoice.uuid ? "#f5f5f5" : "transparent",
                  borderBottom: "1px solid #f0f0f0",
                  "&:hover": {
                    backgroundColor: "#fafafa",
                  },
                }}
              >
                <ListItemButton
                  onClick={() => handleSelectInvoice(invoice)}
                  sx={{ py: 1 }}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedInvoice?.uuid === invoice.uuid}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText
                    primary={invoice.invoiceNumber || "N/A"}
                    secondary={`Order: ${invoice.orderNumber || "N/A"} | Customer: ${
                      invoice.customer?.name || "N/A"
                    } | Status: ${invoice.status || "N/A"}`}
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1rem",
                      color: "primary.main",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, pt:0 }}>
        <Button onClick={handleClose} disabled={isAdding}>
          Cancel
        </Button>
        <Button
          onClick={handleAddOrder}
          variant="contained"
          disabled={!selectedInvoice || isAdding}
          startIcon={isAdding ? <CircularProgress size={16} /> : null}
        >
          {isAdding ? "Adding..." : "Add Order"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrdersToInvoiceModal;
